// src/pages/AddListing/ImageUpload.jsx
import React, { useRef } from 'react';
import { addListingStyles } from './styles';

// Modern SVG Icons
const UploadCloudIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ImageUpload = ({ 
  files, 
  setFiles, 
  previews, 
  setPreviews, 
  error, 
  setError, 
  loading 
}) => {
  const dropRef = useRef();
  const fileInputRef = useRef();

  const handleFiles = (e) => {
    setError("");
    const selected = Array.from(e.target.files || []);
    if (files.length + selected.length > 10) {
      setError("You can upload up to 10 images.");
      return;
    }
    setFiles(prev => [...prev, ...selected]);
    setPreviews(prev => [...prev, ...selected.map(file => URL.createObjectURL(file))]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setError("");
    const dropped = Array.from(e.dataTransfer.files || []);
    if (files.length + dropped.length > 10) {
      setError("You can upload up to 10 images.");
      return;
    }
    setFiles(prev => [...prev, ...dropped]);
    setPreviews(prev => [...prev, ...dropped.map(file => URL.createObjectURL(file))]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#CE1126';
    e.currentTarget.style.backgroundColor = 'rgba(206, 17, 38, 0.03)';
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.borderColor = '#E5E5E5';
    e.currentTarget.style.backgroundColor = '#FFFFFF';
  };

  const handleRemoveImage = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
    setPreviews(previews.filter((_, i) => i !== idx));
  };

  return (
    <div style={addListingStyles.uploadSection}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        disabled={loading}
        style={addListingStyles.hiddenFileInput}
      />
      
      <div
        style={addListingStyles.dropZone}
        ref={dropRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadCloudIcon />
        <h3 style={addListingStyles.dropZoneTitle}>
          Upload Photos
        </h3>
        <p style={addListingStyles.dropZoneText}>
          Drag and drop images here, or click to browse
        </p>
        <p style={addListingStyles.dropZoneHint}>
          <ImageIcon /> Up to 10 images • JPG, PNG, or GIF
        </p>
      </div>
      
      {previews.length > 0 && (
        <div style={addListingStyles.previewSection}>
          <div style={addListingStyles.previewHeader}>
            <span style={addListingStyles.previewCount}>
              {files.length} image{files.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div style={addListingStyles.previewGrid}>
            {previews.map((preview, idx) => (
              <div key={idx} style={addListingStyles.previewCard}>
                <img
                  src={preview}
                  alt={`Preview ${idx + 1}`}
                  style={addListingStyles.previewImage}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(idx);
                  }}
                  style={addListingStyles.removeButton}
                  disabled={loading}
                  title="Remove image"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#CE1126';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <TrashIcon />
                </button>
                {idx === 0 && (
                  <div style={addListingStyles.primaryBadge}>
                    Primary
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;